import { ARENA } from './balance'
import type { AdvCollage } from './adv'

// 切り貼りの紙芝居 (2026-10-30 プロローグ試作)。 紙の地に、 白い縁の紙人形を貼っていく。
//   作者「絵を切り貼りしたようなデザインで、この城がどういう状況なのか伝える」(手本＝作者が示した動画 1:17〜1:33)。
//   1枚ごとにぱっと替わり、 替わる時だけ紙の白が一瞬かぶる。 部品は Codex が1つずつ描いた切り絵 (public/adv/prologue/collage/)。
//   窓の明かりは常夜燈の青白い色 (10-11 作者「常夜燈の色は青っぽい色」)。 光の筋・白い線は描かない。
// 状態を持たず、 page と el (貼ってからの ms) だけで決める。
const DIR = '/adv/prologue/collage/'
// 騎士・花嫁の紙人形 (knight/bride.webp) は 10-30 夕に使うのをやめた (作者「壁の裏の騎士と花嫁は出さない」)。 絵は残してある。
const NAMES = ['castle', 'cloak', 'crown', 'lock', 'candle', 'lunaire']
export const ADV_COLLAGE_PARTS = NAMES.map(n => `${DIR}${n}.webp`)
const images = new Map<string, HTMLImageElement>()
function part(name: string): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null
  const src = `${DIR}${name}.webp`
  let img = images.get(src)
  if (!img) { img = new Image(); img.src = src; images.set(src, img) }
  return img.complete && img.naturalWidth ? img : null
}
export function preloadAdvCollage(): void { for (const n of NAMES) part(n) }

const clamp01 = (t: number) => Math.min(1, Math.max(0, t))
const easeOut = (t: number) => 1 - (1 - t) ** 3
// 少し行きすぎて戻る (紙をぱたんと立てた時の弾み)。
const easeBack = (t: number) => { const c = 1.9; return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2 }
const hash = (i: number, k: number) => { const s = Math.sin(i * 127.1 + k * 311.7) * 43758.5453; return s - Math.floor(s) }

// 紙の地 (一度だけ作る)。 少し黄ばんだ紙に、 細かな繊維のむらと四隅の陰。
let paper: HTMLCanvasElement | null = null
function paperGround(): HTMLCanvasElement | null {
  if (paper || typeof document === 'undefined') return paper
  const c = document.createElement('canvas'); c.width = ARENA.width; c.height = ARENA.height
  const g = c.getContext('2d'); if (!g) return null
  g.fillStyle = '#ddd6c8'; g.fillRect(0, 0, c.width, c.height)
  for (let i = 0; i < 2600; i++) {
    const d = hash(i, 3) < .5
    g.fillStyle = d ? `rgba(120,104,84,${.03 + hash(i, 4) * .05})` : `rgba(255,252,244,${.05 + hash(i, 4) * .07})`
    g.fillRect(hash(i, 1) * c.width, hash(i, 2) * c.height, 1 + hash(i, 5) * 3, 1 + hash(i, 6) * 1.5)
  }
  const v = g.createRadialGradient(c.width / 2, c.height * .45, c.height * .35, c.width / 2, c.height * .45, c.width * .7)
  v.addColorStop(0, 'rgba(60,48,40,0)'); v.addColorStop(1, 'rgba(60,48,40,.32)')
  g.fillStyle = v; g.fillRect(0, 0, c.width, c.height)
  paper = c
  return c
}

// 紙人形を1枚貼る。 (cx, bottom) は足元の中心・h は高さ。 sy＝縦の伸び (立てる途中)・rot＝傾き。 影を少し落として紙が浮いて見えるようにする。
type Place = { cx: number; bottom: number; h: number; sy?: number; s?: number; fx?: number; rot?: number; alpha?: number }
function paste(ctx: CanvasRenderingContext2D, img: HTMLImageElement, p: Place): { x: number; y: number; w: number; h: number } {
  const h = p.h, w = img.naturalWidth * h / img.naturalHeight
  const box = { x: p.cx - w / 2, y: p.bottom - h, w, h }
  ctx.save()
  ctx.globalAlpha = p.alpha ?? 1
  ctx.translate(p.cx, p.bottom)
  if (p.rot) ctx.rotate(p.rot)
  ctx.scale((p.s ?? 1) * (p.fx ?? 1), (p.s ?? 1) * (p.sy ?? 1))
  ctx.shadowColor = 'rgba(40,30,24,.38)'; ctx.shadowBlur = 8; ctx.shadowOffsetX = 5; ctx.shadowOffsetY = 7
  ctx.drawImage(img, -w / 2, -h, w, h)
  ctx.restore()
  return box
}
// 丸い窓の明かり。 中を青白く塗り、 まわりへ淡くにじませる (線は引かない)。
function windowLight(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number): void {
  if (a <= 0) return
  ctx.save()
  const halo = ctx.createRadialGradient(x, y, r * .6, x, y, r * 3)
  halo.addColorStop(0, `rgba(150,196,255,${.45 * a})`); halo.addColorStop(1, 'rgba(150,196,255,0)')
  ctx.fillStyle = halo; ctx.beginPath(); ctx.arc(x, y, r * 3, 0, Math.PI * 2); ctx.fill()
  const core = ctx.createRadialGradient(x, y - r * .2, 0, x, y, r)
  core.addColorStop(0, `rgba(226,240,255,${a})`); core.addColorStop(1, `rgba(140,186,240,${a})`)
  ctx.fillStyle = core; ctx.beginPath(); ctx.arc(x, y, r * .98, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}
// 切り絵の城で測った窓と門の位置 (絵に対する割合・r は絵の幅に対する割合)。 灯る順＝真ん中→内側→外側。
const WINDOWS = [
  { x: .500, y: .561, r: .0285 }, { x: .389, y: .599, r: .0195 }, { x: .611, y: .599, r: .0195 },
  { x: .125, y: .599, r: .0195 }, { x: .876, y: .599, r: .0195 },
]
const GATE = { x: .504, y: .864 }
const CASTLE = { cx: ARENA.width / 2, bottom: ARENA.height * .8, h: ARENA.height * .66 }

type Box = { x: number; y: number; w: number; h: number }
function castleWithWindows(ctx: CanvasRenderingContext2D, el: number, lit: (i: number) => number, rise: boolean, at: Place = CASTLE): Box | null {
  const img = part('castle'); if (!img) return null
  const u = rise ? clamp01(el / 300) : 1
  const box = paste(ctx, img, { ...at, sy: rise ? Math.max(.02, easeBack(u)) : 1 })
  if (u >= 1) WINDOWS.forEach((w, i) => windowLight(ctx, box.x + w.x * box.w, box.y + w.y * box.h, w.r * box.w, lit(i)))
  return box
}
// 余白の小道具は、 ぽんと置かれる (少し大きく出て縮む)。
function pop(ctx: CanvasRenderingContext2D, name: string, at: number, el: number, p: Place): void {
  const img = part(name); if (!img || el < at) return
  const u = clamp01((el - at) / 220)
  paste(ctx, img, { ...p, s: 1 + .25 * (1 - easeOut(u)), alpha: clamp01(u * 3) })
}
// 外套をかぶせた城 (3枚目の終わりの形)。 drop＝外套が落ちきるまで・dim＝窓が翳るまで・lockAt＝錠を押す時刻。
//   sleep＝真ん中の窓の灯りが消える進み (斬れば眠れる)。 外套の頂点の位置を返す (王冠を載せる)。
const CLOAK_LAND = .52
function coveredCastle(ctx: CanvasRenderingContext2D, el: number, at: Place, o: { drop: number; dim: number; lockAt: number; sleep?: number }): { box: Box; apex: number } | null {
  const box = castleWithWindows(ctx, el, i => (1 - .72 * o.dim) * (i === 0 ? 1 - (o.sleep ?? 0) : 1), false, at)
  if (!box) return null
  if (o.dim > 0) { ctx.save(); ctx.globalAlpha = .22 * o.dim; ctx.fillStyle = '#1a1830'; ctx.fillRect(0, 0, ARENA.width, ARENA.height); ctx.restore() }
  const cloak = part('cloak')
  let apex = box.y
  if (cloak && o.drop > 0) {
    const h = box.h * .78, land = box.y + box.h * CLOAK_LAND, s = box.w * 1.08 / (cloak.naturalWidth * h / cloak.naturalHeight)
    const bottom = land - (1 - easeBack(o.drop)) * (land + 40)
    paste(ctx, cloak, { cx: box.x + box.w / 2, bottom, h, s })
    apex = bottom - h * s
  }
  const lock = part('lock')
  if (lock && el >= o.lockAt) {
    const u = clamp01((el - o.lockAt) / 160)
    paste(ctx, lock, { cx: box.x + GATE.x * box.w, bottom: box.y + GATE.y * box.h + box.h * .1, h: box.h * .28, s: 1.4 - .4 * easeOut(u), alpha: clamp01(u * 4) })
  }
  return { box, apex }
}

// 4枚目からの舞台：外套をかぶった城は右へ寄り、 左に主人公の紙人形が立つ。 文字窓 (.74H から下) に足元がかかってもよいよう、 上へ詰める。
const CASTLE2: Place = { cx: ARENA.width * .63, bottom: ARENA.height * .73, h: ARENA.height * .45 }
const LUN: Place = { cx: ARENA.width * .17, bottom: ARENA.height * .73, h: ARENA.height * .45 }
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
type Court = {
  move?: number          // 0＝3枚目の位置・1＝右へ寄った位置
  crown?: number         // 王冠が外套の頂点へ落ちる進み (0 なら無し)
  lun?: number           // 主人公が左から入る進み (0 なら無し)
  fx?: number            // 主人公の向き (1＝城の方・-1＝背を向ける)
  sleep?: number         // 真ん中の窓の灯りが消える進み
  ages?: number          // 月が城のまわりを回り始めてからの ms (負なら無し)
  bubble?: number        // 吹き出しを飛ばしてからの ms (負なら無し)
}
function court(ctx: CanvasRenderingContext2D, c: Court): void {
  const m = easeOut(clamp01(c.move ?? 1))
  const at: Place = { cx: lerp(CASTLE.cx, CASTLE2.cx, m), bottom: lerp(CASTLE.bottom, CASTLE2.bottom, m), h: lerp(CASTLE.h, CASTLE2.h, m) }
  const ages = c.ages ?? -1
  // 夜が明けないまま、 月だけが城の後ろをぐるぐる回る (下から右→上→左へ。 だんだん速く)。
  //   10-30 夕 作者「城でずっと夜が明けず、月がぐるぐる回る」。 下半分は城と文字窓の陰に入る。
  if (ages >= 0) {
    const u = ages / 2000, th = -Math.PI / 2 + Math.PI * 2 * (u + .12 * u * u)
    const x = at.cx + ARENA.width * .34 * Math.cos(th), y = at.bottom - at.h * .5 - ARENA.height * .4 * Math.sin(th)
    ctx.save()
    ctx.shadowColor = 'rgba(40,30,24,.3)'; ctx.shadowBlur = 6; ctx.shadowOffsetX = 3; ctx.shadowOffsetY = 4
    ctx.fillStyle = '#fbfaf5'; ctx.beginPath(); ctx.arc(x, y, 30, 0, Math.PI * 2); ctx.fill()
    ctx.shadowColor = 'transparent'; ctx.fillStyle = '#f3e6b4'; ctx.beginPath(); ctx.arc(x, y, 25, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
  }
  const r = coveredCastle(ctx, 1e9, at, { drop: 1, dim: 1, lockAt: -1, sleep: clamp01(c.sleep ?? 0) })
  const crown = part('crown'), cu = clamp01(c.crown ?? 0)
  if (r && crown && cu > 0) {
    const h = ARENA.height * .11, land = r.apex + h * .5
    paste(ctx, crown, { cx: r.box.x + r.box.w / 2, bottom: land - (1 - easeBack(cu)) * (land + 30), h, rot: .08 })
  }
  const lun = part('lunaire'), lu = clamp01(c.lun ?? 0)
  if (lun && lu > 0) {
    const hop = lu < 1 ? Math.abs(Math.sin(lu * Math.PI * 4)) * 8 : 0
    paste(ctx, lun, { ...LUN, cx: lerp(-ARENA.width * .12, LUN.cx, easeOut(lu)), bottom: LUN.bottom - hop, fx: c.fx ?? 1 })
  }
  // 中身のない吹き出しが窓へ飛び、 窓に当たって落ちる。 城からは何も返らない。
  const b = c.bubble ?? -1
  if (r && b >= 0 && b < 1900) {
    const sx = LUN.cx + ARENA.width * .05, sy = LUN.bottom - LUN.h * .82
    const tx = r.box.x + WINDOWS[0]!.x * r.box.w, ty = r.box.y + WINDOWS[0]!.y * r.box.h
    const u = clamp01(b / 900), e = easeOut(u), fall = clamp01((b - 1250) / 650)
    const x = lerp(sx, tx, e), y = lerp(sy, ty, e) - Math.sin(Math.PI * u) * ARENA.height * .12 + fall * fall * ARENA.height * .3
    const s = (.6 + .4 * e) * (u >= 1 && b < 1050 ? 1 - .12 * Math.sin((b - 900) / 150 * Math.PI) : 1)
    ctx.save(); ctx.globalAlpha = 1 - fall; ctx.translate(x, y); ctx.rotate(fall * .9 - .1 * (1 - u)); ctx.scale(s, s)
    ctx.shadowColor = 'rgba(40,30,24,.35)'; ctx.shadowBlur = 6; ctx.shadowOffsetX = 4; ctx.shadowOffsetY = 5
    ctx.fillStyle = '#fbfaf5'
    ctx.beginPath(); ctx.ellipse(0, 0, 44, 30, 0, 0, Math.PI * 2); ctx.fill()
    ctx.beginPath(); ctx.moveTo(-22, 18); ctx.lineTo(-40, 40); ctx.lineTo(-6, 24); ctx.closePath(); ctx.fill()
    ctx.restore()
  }
}

// 最後の1枚 (月の回る城) を写しておき、 風にめくれて左上へ飛ばす (下に丘の城の絵が見える)。
//   月が跳ばないよう、 最後に描いた月の時刻で写す。
let snap: HTMLCanvasElement | null = null, lastAges = 0
function peel(ctx: CanvasRenderingContext2D, el: number): void {
  if (el >= 1300 || typeof document === 'undefined') return
  if (!snap) { snap = document.createElement('canvas'); snap.width = ARENA.width; snap.height = ARENA.height }
  const g = snap.getContext('2d'); if (!g) return
  drawAdvCollage(g, { page: 'ages', el: lastAges })
  const e = clamp01(el / 1300) ** 2
  ctx.save()
  ctx.translate(-ARENA.width * 1.1 * e, -ARENA.height * .9 * e)
  ctx.rotate(-.5 * e)
  ctx.transform(1, 0, -.25 * e, 1 - .35 * e, 0, ARENA.height * .35 * e)
  ctx.shadowColor = 'rgba(20,16,12,.45)'; ctx.shadowBlur = 18; ctx.shadowOffsetX = 10; ctx.shadowOffsetY = 14
  ctx.drawImage(snap, 0, 0)
  ctx.restore()
}

// 紙の白が一瞬かぶるのは、 構図ごと替わる1枚だけ (続きの1枚は、 前の形からそのまま動く)。
const FLASH_PAGES = new Set(['castle', 'cloak'])
export function drawAdvCollage(ctx: CanvasRenderingContext2D, v: AdvCollage): void {
  const el = v.el
  if (v.page === 'peel') { peel(ctx, el); return }
  ctx.save()
  const ground = paperGround()
  if (ground) ctx.drawImage(ground, 0, 0)
  else { ctx.fillStyle = '#ddd6c8'; ctx.fillRect(0, 0, ARENA.width, ARENA.height) }
  const candle: Place = { cx: ARENA.width * .1, bottom: ARENA.height * .86, h: ARENA.height * .3, rot: -.06 }
  const step = (ms: number) => clamp01(el / ms)
  switch (v.page) {
    case 'castle':
      // 城がぱたんと立ち、 窓が1つずつ灯る。 余白に燭台。
      castleWithWindows(ctx, el, i => clamp01((el - 420 - i * 170) / 160), true)
      pop(ctx, 'candle', 1350, el, candle)
      break
    case 'cloak':
      // 灯った城に外套が上から覆いかぶさり、 窓が翳る。 門に錠が押される。
      pop(ctx, 'candle', -1, el, candle)
      coveredCastle(ctx, el, CASTLE, { drop: clamp01((el - 250) / 420), dim: clamp01((el - 560) / 400), lockAt: 1150 })
      break
    // 外套ごと城が右へ運ばれ、 外套の頂点に王冠が落ちる (王さまは出さない・作者 10-30)。
    case 'taken': court(ctx, { move: step(700), crown: clamp01((el - 650) / 380) }); break
    case 'princess': court(ctx, { crown: 1, lun: step(800) }); break
    // 斬れば眠れる＝真ん中の窓の灯りがすっと消える。 「斬るって」で灯りが戻る。
    case 'sleep': court(ctx, { crown: 1, lun: 1, sleep: clamp01((el - 200) / 700) }); break
    case 'wake': court(ctx, { crown: 1, lun: 1, sleep: 1 - easeOut(step(450)) }); break
    case 'refuse': court(ctx, { crown: 1, lun: 1, fx: Math.cos(Math.PI * step(320)) }); break
    // 城の方へ向き直って呼ぶ。 2度目の呼びかけで、 もう1つ吹き出しが飛ぶ。
    case 'call': court(ctx, { crown: 1, lun: 1, fx: -Math.cos(Math.PI * step(320)), bubble: el - 350 }); break
    case 'callAgain': court(ctx, { crown: 1, lun: 1, bubble: el - 150 }); break
    case 'ages': lastAges = el; court(ctx, { crown: 1, lun: 1, ages: el - 200 }); break
  }
  if (FLASH_PAGES.has(v.page)) {
    const flash = 1 - clamp01(el / 150)
    if (flash > 0) { ctx.fillStyle = `rgba(248,245,238,${flash})`; ctx.fillRect(0, 0, ARENA.width, ARENA.height) }
  }
  ctx.restore()
}
